import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type TabContent = {
  scrollPosition: number;
  itemSizes?: any;
};

type IChannel = {
  channelHashId: string;
  videos: IVideo[];
  playerRef?: any;
  isActivePlayer: boolean;
  lastVideoId: string;
  playerTime?: number;
  lastVideoData: any;
};

type IVideo = {
  videoHashId: string;
  channelHashId: string;
  selectedTab?: number;
  selectedShot?: number;
  tabContent?: Record<string, TabContent>;
  changedShot?: boolean;
  searchKey?: string;
};

export type secondaryChannelVideoState = {
  channels: Record<string, IChannel>; // Update type to store video objects directly within channel objects
};

const initialState: secondaryChannelVideoState = {
  channels: {}
};

const secondaryChannelVideoSlice = createSlice({
  name: 'secondary-channel-videos',
  initialState: initialState,
  reducers: {
    addVideoToChannel(
      state,
      action: PayloadAction<{ channelHashId: string; videoHashId: string; video?: any; tab?: string }>
    ) {
      const { channelHashId, videoHashId, video, tab } = action.payload;

      if (!channelHashId || !videoHashId) {
        console.warn('Invalid channelHashId or videoHashId', action.payload);
        return;
      }

      const videoData = {
        videoHashId,
        channelHashId,
        selectedTab: 0,
        selectedShot: 0,
        changedShot: false
      };

      if (!state.channels[channelHashId]) {
        state.channels[channelHashId] = {
          isActivePlayer: false,
          lastVideoId: videoHashId,
          channelHashId,
          videos: [],
          playerTime: 0,
          lastVideoData: null
        };
      }

      const channel = state.channels[channelHashId];
      const shouldReplace = tab === 'videos' || tab === 'activity';

      if (shouldReplace) {
        channel.videos = [videoData];
      } else {
        const videoExists = channel.videos.some((v) => v.videoHashId === videoHashId);
        if (!videoExists) {
          channel.videos.push(videoData);
        }
      }

      channel.lastVideoId = videoHashId;

      if (video !== undefined) {
        channel.lastVideoData = video;
      }
    },

    setSelectedTab(state, action: PayloadAction<{ channelHashId: string; videoHashId: string; tab: number }>) {
      const { channelHashId, videoHashId, tab } = action.payload;
      const channel = state.channels[channelHashId];

      if (channel) {
        // Find the video by its videoHashId
        const video = channel.videos.find((video) => video.videoHashId === videoHashId);
        if (video) {
          // Update the selectedTab for the video
          video.selectedTab = tab;
        }
      }
    },
    setSelectedShot(
      state,
      action: PayloadAction<{ channelHashId: string; videoHashId: string; newSelectedShot: number }>
    ) {
      const { channelHashId, videoHashId, newSelectedShot } = action.payload;
      const channel = state.channels[channelHashId];

      if (channel) {
        // Find the video by its videoHashId
        const video = channel.videos.find((video) => video.videoHashId === videoHashId);
        if (video) {
          // Update the selectedTab for the video
          video.selectedShot = newSelectedShot;
        }
      }
    },
    toggleActivePlayer(state, action) {
      const { channelHashId, activePlayer } = action.payload;
      const channel = state.channels[channelHashId];
      if (channel) {
        channel.isActivePlayer = activePlayer;
      }
    },
    setPlayerRef(state, action) {
      const { channelHashId, playerRef } = action.payload;
      const channel = state.channels[channelHashId];
      if (channel) {
        channel.playerRef = playerRef;
      }
    },
    seekPlayerTime(state, action) {
      const { channelHashId, time } = action.payload;
      const channel = state.channels[channelHashId];
      if (channel) {
        channel.playerRef.seek(time);
      }
    },
    setPlayerTime(state, action: PayloadAction<{ channelHashId: string; currentTime: number }>) {
      const { channelHashId, currentTime } = action.payload;
      const channel = state.channels[channelHashId];
      if (channel) {
        channel.playerTime = currentTime;
      }
    },
    removeChannel(state, action: PayloadAction<{ channelId: string }>) {
      const { channelId } = action.payload;
      delete state.channels[channelId];
    },
    changeVideoShot(state, action) {
      const { channelHashId, videoShots, selectedShot } = action.payload;
      const channel = state.channels[channelHashId];
      try {
        const source = videoShots[selectedShot];
        if (channel && source) {
          channel.playerRef
            .load({
              hls: source.hls_location,
              subtitleTracks: [
                {
                  id: source.id,
                  label: 'English',
                  lang: 'en',
                  kind: 'captions',
                  url: source.transcript // URL to your closed captions file (e.g., VTT file)
                }
              ]
            })
            .then(() => {
              try {
                channel.playerRef.seek(0);
              } catch (err) {
                console.log('err');
              }
            });
        }
      } catch (err) {
        console.log(err);
      }
    },
    updateTabContent(
      state,
      action: PayloadAction<{
        channelHashId: string;
        videoHashId: string;
        data: { [tabName: string]: { scrollPosition: number; itemSizes?: any } };
      }>
    ) {
      const { channelHashId, videoHashId, data } = action.payload;
      const channel = state.channels[channelHashId];
      if (channel) {
        const video = channel.videos.find((video) => video.videoHashId === videoHashId);
        if (video) {
          // Update the selectedTab for the video
          video.tabContent = {
            ...video.tabContent,
            ...data
          };
        }
      }
    },
    updateSecondaryViewVideoChannels: (
      state,
      action: PayloadAction<{
        channelId: string;
        videoHashId: string;
        channel: any;
        type: 'add' | 'remove' | 'archived' | 'reshared';
      }>
    ) => {
      const { channelId, videoHashId, channel, type } = action.payload;

      // Apply the logic to both target channel and 'home'
      const targetChannelIds = [channelId, 'home'];

      targetChannelIds.forEach((id) => {
        const channelObj = state.channels[id];
        if (channelObj && channelObj.lastVideoId === videoHashId) {
          let updatedChannels = channelObj.lastVideoData?.channels || [];

          if (type === 'add') {
            const exists = updatedChannels.some((ch: any) => ch.channel.id === channel.id);
            if (!exists) {
              updatedChannels = [...updatedChannels, { channel, is_archived: false }];
            }
          } else if (type === 'remove') {
            updatedChannels = updatedChannels.filter((ch: any) => ch.channel.id !== channel.id);
          } else if (type === 'archived') {
            updatedChannels = updatedChannels.map((ch: any) =>
              ch.channel.id === channel.id ? { ...ch, is_archived: true } : ch
            );
          } else if (type === 'reshared') {
            updatedChannels = updatedChannels.map((ch: any) =>
              ch.channel.id === channel.id ? { ...ch, is_archived: false } : ch
            );
          }

          channelObj.lastVideoData = {
            ...channelObj.lastVideoData,
            channels: updatedChannels
          };
        }
      });
    },
    updateSecondaryViewVideoField: (
      state,
      action: PayloadAction<{ channelId: string; videoHashId: string; field: string; value: any }>
    ) => {
      const { channelId, videoHashId, field, value } = action.payload;
      console.log('payload updateSecondaryViewVideoField', action.payload);
      const channel = state.channels[channelId];
      if (channel && channel?.lastVideoData?.hash_id === videoHashId) {
        channel.lastVideoData = {
          ...channel.lastVideoData,
          [field]: value // Update only the specified field
        };
      }
    }
  }
});

export const getLastVideoByChannel = (state: secondaryChannelVideoState, channelHashId: string): IVideo | null => {
  const channel = state.channels[channelHashId];
  if (!channel || channel.videos.length === 0) {
    return null; // No channel or no videos available
  }
  return channel.videos.find((video) => video.videoHashId === channel.lastVideoId) || null;
};

export const getScrollPosition = (
  state: secondaryChannelVideoState,
  channelId: string,
  videoHashId: string,
  tabName: string
): number | undefined => {
  // Retrieve the video object with the specified channelId
  const channel = state.channels[channelId];
  // Check if the video and tabContent exist
  if (channel) {
    // If the tabContent for the specified tabName exists, return its scrollPosition
    const video = channel.videos.find((video) => video.videoHashId === videoHashId);
    if (video && video.tabContent) {
      return video.tabContent[tabName]?.scrollPosition;
    }
  } else {
    return undefined;
  }
};

export const getItemSizes = (
  state: secondaryChannelVideoState,
  channelId: string,
  videoHashId: string,
  tabName: string
): number | undefined => {
  // Retrieve the video object with the specified channelId
  const channel = state.channels[channelId];
  // Check if the channel and tabContent exist
  if (channel) {
    const video = channel.videos.find((video) => video.videoHashId === videoHashId);
    if (video && video.tabContent) {
      return video.tabContent[tabName]?.itemSizes;
    }
    // If the tabContent for the specified tabName exists, return its scrollPosition
  } else {
    return undefined;
  }
};

export const {
  addVideoToChannel,
  setSelectedTab,
  setSelectedShot,
  toggleActivePlayer,
  setPlayerRef,
  seekPlayerTime,
  setPlayerTime,
  removeChannel,
  changeVideoShot,
  updateTabContent,
  updateSecondaryViewVideoChannels,
  updateSecondaryViewVideoField
} = secondaryChannelVideoSlice.actions;

export const secondaryChannelVideoReducers = secondaryChannelVideoSlice.reducer;
